import { useEffect, useState } from 'react';

import { Loader } from '@amazd/common/components';
import { useTranslation } from '@amazd/common/utils/i18n';
import { Autocomplete, Box, Button, Chip, FormHelperText, Grid, Switch, TextField, Typography } from '@mui/material';
import AvatarPicker from 'components/AvatarPicker';
import Control from 'components/Control';
import { CompanyImageUploadPurposeEnum } from 'hooks/useCompanyImageUpload';
import { useSnackbar } from 'hooks/useSnackbar';
import { Controller } from 'react-hook-form';
import useCompanyUpdateMutation from 'views/AdminView/views/CompaniesView/CompanyForm/hooks/useCompanyUpdateMutation';

import useCompanyForm from './hooks/useCompanyForm';
import useCompanyQuery from './hooks/useCompanyQuery';
import useStyles from './styles';

interface CompanyProfileFormProps {
  companyId: string;
}

const CompanyProfileForm = ({ companyId }: CompanyProfileFormProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const { company, loading, error, refetch } = useCompanyQuery(companyId);
  const { updateCompany } = useCompanyUpdateMutation();
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, dirtyFields, isDirty },
    control,
    reset,
    getValues,
    setValue,
    setError,
  } = useCompanyForm(company);

  useEffect(() => {
    reset(company);
  }, [company, reset]);

  useEffect(() => {
    if (error) {
      showSnackbar(error.message);
    }
  }, [error, showSnackbar]);

  const onSubmit = async (data: any) => {
    const dirtyValues = Object.keys(dirtyFields).reduce((acc, key) => {
      return {
        ...acc,
        [key]: data[key],
      };
    }, {});

    setSubmitting(true);
    const result = await updateCompany({
      variables: {
        id: companyId,
        data: dirtyValues,
      },
    });
    setSubmitting(false);
    if (!result.errors) {
      refetch();
      showSnackbar(
        t('company-settings-profile-message-success-update'),
        'success',
        t('company-settings-profile-message-success-update-title'),
      );
    } else {
      const error = result.errors[0];
      const validationErrors: any = error.extensions?.validationErrors;
      if (validationErrors) {
        validationErrors.forEach((error: any) => {
          const message = Object.values(error.constraints).join(', ');
          setError(error.property, { type: 'manual', message });
        });
      } else {
        showSnackbar(error.message);
      }
    }
  };

  const registerWithError = (name: any) => {
    const errorMessage = (errors as any)[name]?.message;
    return {
      ...register(name),
      helperText: errorMessage,
    };
  };

  if (loading) return <Loader />;

  return (
    <>
      <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
        <Box className={classes.paper}>
          <Grid>
            <Typography variant="h4" className={classes.headerText}>
              {t('company-settings-profile-title')}
            </Typography>
            <Control label={t('company-settings-profile-field-company-logo')} className={classes.avatarControl}>
              <AvatarPicker
                companyId={companyId}
                purpose={CompanyImageUploadPurposeEnum.COMPANY_AVATAR}
                value={getValues().image}
                defaultValue="/amazd-logo-big.png"
                onChange={(value) => {
                  setValue('image', value, { shouldDirty: true });
                }}
                onRemove={() => setValue('image', undefined, { shouldDirty: true })}
              />
            </Control>
            <div className={classes.grid}>
              <Control label={t('company-settings-profile-field-name')}>
                <TextField {...registerWithError('name')} />
              </Control>
              <Control
                label={t('company-settings-profile-field-handle')}
                tooltipInfo={t('team-user-form-handle-tooltip')}
              >
                <Typography className={classes.handle}>@{getValues().handle}</Typography>
              </Control>
            </div>
            <Control label={t('company-settings-profile-field-description')}>
              <TextField multiline {...registerWithError('description')} />
            </Control>

            <Control
              label={t('company-settings-profile-field-tags')}
              subLabel={t('company-settings-profile-field-tags-sub-label')}
              className={classes.marginTop32}
            >
              <Controller
                name="tags"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    value={field.value ?? []}
                    onChange={(_event: any, newValues: any) => field.onChange(newValues)}
                    multiple
                    freeSolo
                    options={[]}
                    disableClearable
                    renderTags={(tagValue, getTagProps) =>
                      tagValue.map((code, index) => (
                        <Chip {...getTagProps({ index })} key={`${code}`} color="primary" label={`${code}`} />
                      ))
                    }
                    renderInput={(params) => {
                      return <TextField {...params} variant="outlined" contentEditable={false} />;
                    }}
                  />
                )}
              />
              <FormHelperText>{errors?.tags?.map?.((x) => x?.message).join('; ')}</FormHelperText>
            </Control>
            <Control label={t('company-settings-profile-field-call-length')} className={classes.marginTop32}>
              <TextField
                key={(getValues().durations || ['']).toString()}
                className={classes.callLengthInput}
                defaultValue={getValues().durations?.join(',') || ''}
                onChange={(e: any) => {
                  setValue('durations', e.target.value.split(','), { shouldDirty: true });
                }}
              />
            </Control>
            <Control label={t('company-settings-profile-field-uses-informal-language')}>
              <Switch
                color="secondary"
                checked={getValues().usesInformalLanguage}
                onChange={(e) => {
                  setValue('usesInformalLanguage', e.target.checked, { shouldDirty: true });
                }}
              />
              <FormHelperText>{errors.usesInformalLanguage?.message}</FormHelperText>
            </Control>
          </Grid>
        </Box>
        <Box className={classes.actions}>
          {isDirty && (
            <Button
              className={classes.button}
              onClick={() => reset()}
              disabled={!isDirty || submitting}
              color="primary"
            >
              {t('form-cancel')}
            </Button>
          )}
          <Button
            className={classes.button}
            disabled={!isDirty || submitting}
            onClick={handleSubmit(onSubmit)}
            type={'submit'}
            color="primary"
            variant="contained"
          >
            {t('form-save')}
          </Button>
        </Box>
      </form>
    </>
  );
};

export default CompanyProfileForm;
